import { createBrowserRouter, Outlet, RouterProvider, useRouteError } from 'react-router-dom';
import { Flex } from '@atoms/layout';
import { AuthGuard } from '@molecules/auth/authGuard';
import { ErrorMessage } from '@organisms/dataDisplay';
import { ComingSoonPage } from '@organisms/dataDisplay';
import { MainLayout } from '@templates/mainLayout';
import { ChatwootWrapper } from '@shiftnex/framework/src/components/molecules/supportChat';
import { SupportChatUserContextProvider } from '@molecules/supportChat/userContextProvider';

export function Routing() {
  return <RouterProvider router={router} />;
}

const router = createBrowserRouter([
  {
    path: '/login',
    async lazy() {
      const { SignInPage } = await import('@pages/auth');
      return { Component: SignInPage };
    },
  },
  {
    path: '/otp',
    async lazy() {
      const { OtpPage } = await import('@pages/auth');
      return { Component: OtpPage };
    },
  },
  {
    path: '/register',
    element: <Outlet />,
    children: [
      {
        path: '/register',
        async lazy() {
          const { AdminSignUp } = await import('@pages/auth');
          return { Component: AdminSignUp };
        },
      },
      {
        path: '/register/details',
        async lazy() {
          const { AdminSignUpDetails } = await import('@pages/auth');
          return { Component: AdminSignUpDetails };
        },
      },
      {
        path: '/register/address',
        async lazy() {
          const { AdminSignUpAddress } = await import('@pages/auth');
          return { Component: AdminSignUpAddress };
        },
      },
    ],
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <SupportChatUserContextProvider>
          <ChatwootWrapper>
            <MainLayout>
              <Outlet />
            </MainLayout>
          </ChatwootWrapper>
        </SupportChatUserContextProvider>
      </AuthGuard>
    ),
    // TODO: make 404 page
    ErrorBoundary: () => {
      const error = useRouteError() as Error;
      // yes - this ^ method doesn't return a formatter error with normal adequate error codes.
      // So we have to check the message
      if (error && error.message && error.message.includes('Failed to fetch dynamically imported module')) {
        window.location.reload(); // reload page in order to attempt to get a new one build
        return;
      }
      console.error('ERROR', error);
      console.info('ERROR', { err: error });
      return (
        <>
          <MainLayout>
            <ErrorMessage error={error} />
          </MainLayout>
        </>
      );
    },
    children: [
      {
        path: '/',
        lazy: () => import('@pages/dashboard'),
      },
      {
        path: '/shifts',
        async lazy() {
          const { ShiftsList } = await import('@pages/shifts');
          return { Component: ShiftsList };
        },
      },
      {
        path: '/shifts/series-n-rotations',
        async lazy() {
          const { SeriesNRotationsList } = await import('@pages/shifts');
          return { Component: SeriesNRotationsList };
        },
      },
      {
        path: '/providers',
        lazy: () => import('@pages/talents'),
      },
      {
        path: '/clients',
        lazy: () => import('@pages/clients'),
        children: [
          {
            path: '/clients/locations',
            lazy: () => import('@pages/clients'),
          },
        ],
      },
      {
        path: '/attendance',
        lazy: () => import('@pages/timecards'),
      },
      {
        path: '/settings',
        async lazy() {
          const { SettingsRoot } = await import('@pages/settings');
          return { Component: SettingsRoot };
        },
        children: [
          {
            path: '/settings/organization',
            async lazy() {
              const { OrganizationSettings } = await import('@pages/settings');
              return { Component: OrganizationSettings };
            },
            children: [
              {
                path: '/settings/organization/details',
                async lazy() {
                  const { OrganizationDetails } = await import('@pages/settings');
                  return { Component: OrganizationDetails };
                },
              },
            ],
          },
          {
            path: '/settings/users',
            async lazy() {
              const { UsersSettingsPage } = await import('@pages/settings');
              return { Component: UsersSettingsPage };
            },
            children: [],
          },
          {
            path: '/settings/qualifications-specialties',
            async lazy() {
              const { QualificationSpecialtiesDummy } = await import('@pages/settings');
              return { Component: QualificationSpecialtiesDummy };
            },
            children: [
              {
                path: '/settings/qualifications-specialties/qualifications',
                async lazy() {
                  const { Qualifications } = await import('@pages/settings');
                  return { Component: Qualifications };
                },
              },
              {
                path: '/settings/qualifications-specialties/specialties',
                async lazy() {
                  const { Specialties } = await import('@pages/settings');
                  return { Component: Specialties };
                },
              },
            ],
          },
          {
            path: '/settings/locations',
            async lazy() {
              const { Locations } = await import('@pages/settings');
              return { Component: Locations };
            },
          },
          {
            path: '/settings/locations/:id',
            async lazy() {
              const { EditLocation } = await import('@pages/settings');
              return { Component: EditLocation };
            },
          },
          {
            path: '/settings/locations/new',
            async lazy() {
              const { NewLocation } = await import('@pages/settings');
              return { Component: NewLocation };
            },
          },
          {
            path: '/settings/*',
            element: (
              <Flex justifyCenter alignCenter height="100%" width="100%">
                <ComingSoonPage />
              </Flex>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/*',
    element: (
      <MainLayout>
        <ComingSoonPage />
      </MainLayout>
    ),
  },
]);
